import React, { useState, useMemo } from "react";
import { Button, FormFieldSet } from "../../../../../components";

const CATEGORIES = {
  data: { label: "Data", color: "#EFF6FF", textColor: "#1D4ED8" },
  ui: { label: "UI", color: "#F0FDF4", textColor: "#15803D" },
  event: { label: "Events", color: "#FFFBEB", textColor: "#B45309" },
  filter: { label: "Filter", color: "#FDF2F8", textColor: "#9D174D" },
  server: { label: "Server", color: "#FFF7ED", textColor: "#C2410C" },
  layout: { label: "Layout", color: "#F9FAFB", textColor: "#374151" },
};

const PROPS = [
  // Data
  {
    prop: "columns",
    cat: "data",
    def: "[]",
    type: "Array",
    desc: "Each column defines key, label, render, width, dataType, dataValue, total, totalRender, sortfun, hide, columnMenu, getCellClass, getRowClass.",
  },
  {
    prop: "rows",
    cat: "data",
    def: "[]",
    type: "Array",
    desc: "Mapped row data. Pass through mapTableRows(rows, columns) before sending in.",
  },
  {
    prop: "defaultSelectedRows",
    cat: "data",
    def: "[]",
    type: "Array of row IDs",
    desc: "Pre-selected row IDs on mount. Synced when the array changes externally.",
  },
  {
    prop: "expandedRows",
    cat: "data",
    def: "{}",
    type: "{ [rowId]: type }",
    desc: "Controls which rows are expanded and which accordion type to show. Manage with useState + your toggleAccordion function.",
  },
  // UI
  {
    prop: "color",
    cat: "ui",
    def: '"primary"',
    type: "string",
    desc: "Tailwind color name used for header background, active cell border, and selected states.",
  },
  {
    prop: "showSlNo",
    cat: "ui",
    def: "false",
    type: "boolean",
    desc: "Shows a serial number (S.No) column as the first column.",
  },
  {
    prop: "showCheckbox",
    cat: "ui",
    def: "false",
    type: "boolean",
    desc: "Adds a checkbox column for row selection with a select-all checkbox in the header.",
  },
  {
    prop: "showPages",
    cat: "ui",
    def: "true",
    type: "boolean",
    desc: "Shows page number buttons in the toolbar. Disable to show only prev/next arrows.",
  },
  {
    prop: "hideToolBar",
    cat: "ui",
    def: "false",
    type: "boolean",
    desc: "Hides the entire top toolbar including pagination, search, filter, and action buttons.",
  },
  {
    prop: "singleSelection",
    cat: "ui",
    def: "false",
    type: "boolean",
    desc: "Restricts selection to one row at a time. Selecting a new row deselects the previous.",
  },
  {
    prop: "disableSelection",
    cat: "ui",
    def: "false",
    type: "boolean",
    desc: "Disables all checkboxes globally so no row can be selected.",
  },
  {
    prop: "disableRowSelection",
    cat: "ui",
    def: "null",
    type: "(row) => boolean",
    desc: "Per-row disable function. Return true to disable that row's checkbox. Select-all skips disabled rows.",
  },
  {
    prop: "totalSelectedCount",
    cat: "ui",
    def: "null",
    type: "number | null",
    desc: "Override the displayed selected count. Useful for server-side selection tracking.",
  },
  {
    prop: "totalText",
    cat: "ui",
    def: '"Total"',
    type: "string",
    desc: "Label shown in the footer row before the total values.",
  },
  {
    prop: "totalRowStyle",
    cat: "ui",
    def: '""',
    type: "string",
    desc: "Additional Tailwind classes applied to the footer total row.",
  },
  {
    prop: "totalCellStyle",
    cat: "ui",
    def: '"bg-info-900 text-white font-semibold"',
    type: "string",
    desc: "Tailwind classes applied to total value cells in the footer.",
  },
  {
    prop: "noImage",
    cat: "ui",
    def: "undefined",
    type: "boolean",
    desc: "Hides the illustration inside the NoData empty-state component.",
  },
  {
    prop: "noDataDescription",
    cat: "ui",
    def: "undefined",
    type: "string",
    desc: "Custom description text shown in the empty-state when no rows match.",
  },
  {
    prop: "enableKeyboardNavigation",
    cat: "ui",
    def: "false",
    type: "boolean",
    desc: "Enables arrow-key navigation between cells. Highlights the active cell with a colored border.",
  },
  {
    prop: "tableId",
    cat: "ui",
    def: "null",
    type: "string",
    desc: "Unique ID for this table. Required for column config persistence in localStorage and filter saving.",
  },
  {
    prop: "defaultView",
    cat: "ui",
    def: "undefined",
    type: '"list" | "grid"',
    desc: "Initial view mode when the component mounts. Requires toolbarRight.viewToggle: true.",
  },
  {
    prop: "accordion",
    cat: "ui",
    def: "null",
    type: "(row, type) => ReactNode",
    desc: "Renders an expandable panel below a row. Receives the row object and the accordion type string from expandedRows.",
  },
  {
    prop: "gridViewData",
    cat: "ui",
    def: "null",
    type: "ReactNode",
    desc: "Content rendered when viewMode is 'grid'. Cloned and shown in place of the table.",
  },
  {
    prop: "toolbarRight",
    cat: "ui",
    def: "{ search: true, filter: true }",
    type: "object",
    desc: "Controls right toolbar: search, filter, download, viewToggle, fullScreen, add[], order[]. Use order[] to customise render sequence.",
  },
  {
    prop: "toolbarLeft",
    cat: "ui",
    def: "{ add: [] }",
    type: "object",
    desc: "Controls left toolbar items. Each add item supports icon, onClick, or a custom component.",
  },
  // Layout
  {
    prop: "fixedCol",
    cat: "layout",
    def: "0",
    type: "number",
    desc: "Number of columns to pin (sticky) from the left. 0 means no columns are fixed.",
  },
  {
    prop: "page",
    cat: "layout",
    def: "1",
    type: "number",
    desc: "Current page number. Used when serverPagination is true.",
  },
  {
    prop: "limit",
    cat: "layout",
    def: "25",
    type: "number",
    desc: "Initial rows-per-page value.",
  },
  {
    prop: "selectList",
    cat: "layout",
    def: "[25, 50, 100]",
    type: "number[]",
    desc: "Options shown in the rows-per-page dropdown.",
  },
  // Events
  {
    prop: "onSelectionChange",
    cat: "event",
    def: "undefined",
    type: "(ids: any[]) => void",
    desc: "Fires whenever selected row IDs change. Returns the full updated array of selected IDs.",
  },
  {
    prop: "onViewChange",
    cat: "event",
    def: "undefined",
    type: '(mode: "list"|"grid") => void',
    desc: "Fires when the user toggles between list and grid view.",
  },
  {
    prop: "onRowToggle",
    cat: "event",
    def: "null",
    type: "(rowId) => void",
    desc: "Optional callback when an accordion row is toggled. Manage expandedRows state yourself.",
  },
  // Server
  {
    prop: "serverPagination",
    cat: "server",
    def: "false",
    type: "boolean",
    desc: "Enable server-side pagination. The table renders only the rows you pass and fires onPaginationChange for navigation.",
  },
  {
    prop: "totalCount",
    cat: "server",
    def: "null",
    type: "number",
    desc: "Total record count from the server. Required when serverPagination is true to compute page numbers correctly.",
  },
  {
    prop: "onPaginationChange",
    cat: "server",
    def: "() => {}",
    type: "(page: number) => void",
    desc: "Fires when the user changes the current page. Use it to fetch the next batch from the server.",
  },
  {
    prop: "onLimitChange",
    cat: "server",
    def: "() => {}",
    type: "(limit: number) => void",
    desc: "Fires when the user changes rows per page. Use it to re-fetch with the new limit.",
  },
  {
    prop: "serverOnSearch",
    cat: "server",
    def: "undefined",
    type: "(key, value) => void",
    desc: "Search handler for server mode. Called with the debounced input value instead of filtering locally.",
  },
  {
    prop: "serverSearchValue",
    cat: "server",
    def: "undefined",
    type: "string",
    desc: "Controlled search value displayed in the search box when using server-side search.",
  },
  // Filter
  {
    prop: "serverFiltering",
    cat: "filter",
    def: "false",
    type: "boolean",
    desc: "Skips all client-side filter logic and fires onFilterChange with the active filter config instead.",
  },
  {
    prop: "onFilterChange",
    cat: "filter",
    def: "null",
    type: "(filterConfig) => void",
    desc: "Fires when filter conditions change. filterConfig includes allFilterConditions, anyFilterConditions, columns, configName, configId.",
  },
];

const Badge = ({ cat }) => {
  const { label, color, textColor } = CATEGORIES[cat];
  return (
    <span
      style={{
        display: "inline-block",
        fontSize: 11,
        padding: "2px 8px",
        borderRadius: 99,
        background: color,
        color: textColor,
        fontWeight: 500,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </span>
  );
};

const TableAdvProps = () => {
  const [search, setSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");

  const filtered = useMemo(() => {
    const q = search?.toLowerCase().trim();
    return PROPS?.filter((p) => {
      const matchCat = activeFilter === "all" || p?.cat === activeFilter;
      const matchQ =
        !q ||
        p?.prop?.toLowerCase().includes(q) ||
        p?.desc?.toLowerCase().includes(q) ||
        p?.type?.toLowerCase().includes(q);
      return matchCat && matchQ;
    });
  }, [search, activeFilter]);

  const pills = [
    { key: "all", label: "All" },
    ...Object.entries(CATEGORIES)?.map(([k, v]) => ({
      key: k,
      label: v?.label,
    })),
  ];

  return (
    <div className="p-4">
      <h2 className="text-lg font-medium text-gray-800 mb-1">
        TableAdv — Props Reference
      </h2>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <FormFieldSet
          noTitle
          placeholder="Search props…"
          size="sm"
          value={search}
          onChange={(value) => setSearch(value)}
        />
        <div className="flex flex-wrap gap-2">
          {pills?.map(({ key, label }) => (
            <Button
              key={key}
              size="sm"
              onClick={() => setActiveFilter(key)}
              variant="outlined"
              title={label}
              className={`text-xs px-3! py-1! rounded-full border transition-colors ${
                activeFilter === key
                  ? "bg-info-50! text-info-700! border-info-300!"
                  : "bg-gray-50! text-gray-500! border-gray-200! hover:bg-gray-100!"
              }`}
            />
          ))}
        </div>
        <span className="text-xs text-gray-400 ml-auto">
          {filtered?.length} / {PROPS?.length} props
        </span>
      </div>

      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left px-4 py-2.5 font-medium text-gray-500 text-xs whitespace-nowrap">
                Prop
              </th>
              <th className="text-left px-4 py-2.5 font-medium text-gray-500 text-xs whitespace-nowrap">
                Category
              </th>
              <th className="text-left px-4 py-2.5 font-medium text-gray-500 text-xs whitespace-nowrap">
                Default
              </th>
              <th className="text-left px-4 py-2.5 font-medium text-gray-500 text-xs whitespace-nowrap">
                Type
              </th>
              <th className="text-left px-4 py-2.5 font-medium text-gray-500 text-xs">
                Description
              </th>
            </tr>
          </thead>
          <tbody>
            {filtered?.length === 0 ? (
              <tr>
                <td colSpan={5} className="text-center py-10 text-gray-400">
                  No props match your search.
                </td>
              </tr>
            ) : (
              filtered?.map((p) => (
                <tr
                  key={p?.prop}
                  className="border-b border-gray-100 hover:bg-gray-50"
                >
                  <td className="px-4 py-3 align-top whitespace-nowrap">
                    <code className="text-xs font-semibold text-gray-800 bg-gray-100 px-1.5 py-0.5 rounded">
                      {p?.prop}
                    </code>
                  </td>
                  <td className="px-4 py-3 align-top">
                    <Badge cat={p?.cat} />
                  </td>
                  <td className="px-4 py-3 align-top whitespace-nowrap">
                    <code className="text-xs text-gray-500 bg-gray-50 border border-gray-200 px-1.5 py-0.5 rounded">
                      {p?.def}
                    </code>
                  </td>
                  <td className="px-4 py-3 align-top text-xs text-gray-500 whitespace-nowrap max-w-[160px]">
                    {p?.type}
                  </td>
                  <td className="px-4 py-3 align-top text-xs text-gray-600 leading-relaxed">
                    {p?.desc}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TableAdvProps;
